<template>
  <div
    v-if="shouldShowAudioPlayer"
    class="dn-audio-player"
  >
    <div class="dn-audio-player__player-wrap">
      <div
        v-if="canListenToAudio"
        ref="playerContainer"
        class="dn-audio-player__container"
      />
      <div
        v-else
        class="dn-audio-player__placeholder-container"
      >
        <button
          type="button"
          class="dn-audio-player__placeholder-button"
          :aria-label="t('Listen to the article')"
          @click="showLoginTooltip"
        >
          <svg
            width="40"
            height="40"
            viewBox="0.6667 0.6667 26.6667 26.6667"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="dn-audio-player__placeholder-icon"
          >
            <path
              d="M14 0.666748C6.63996 0.666748 0.666626 6.64008 0.666626 14.0001C0.666626 21.3601 6.63996 27.3334 14 27.3334C21.36 27.3334 27.3333 21.3601 27.3333 14.0001C27.3333 6.64008 21.36 0.666748 14 0.666748ZM11.3333 18.6667V9.33341C11.3333 8.78675 11.96 8.46675 12.4 8.80008L18.6266 13.4667C18.9866 13.7334 18.9866 14.2667 18.6266 14.5334L12.4 19.2001C11.96 19.5334 11.3333 19.2134 11.3333 18.6667Z"
            />
          </svg>
        </button>
        <span>{{ t('Listen to the article') }}</span>
      </div>
      <DnTooltip
        v-if="!canListenToAudio && isLoginTooltipOpen"
        :key="loginTooltipKey"
        ref="loginTooltipRef"
        class="dn-audio-player__login-tooltip"
      >
        <template #content>
          <div class="dn-audio-player__login-tooltip-card">
            <button
              type="button"
              class="dn-audio-player__login-tooltip-close"
              :aria-label="t('Close')"
            >
              ×
            </button>
            <h3 class="dn-audio-player__login-tooltip-title">
              {{ t('Listen to the article') }}
            </h3>
            <p class="dn-audio-player__login-tooltip-text">
              {{ loginTooltipText }}
            </p>
            <a
              v-track-event="loginTooltipCtaTrackEvent"
              :href="loginTooltipCtaHref"
              class="dn-audio-player__login-tooltip-cta"
            >
              {{ loginTooltipCtaText }}
            </a>
          </div>
        </template>
        <span
          class="dn-audio-player__tooltip-anchor"
          aria-hidden="true"
        />
      </DnTooltip>
    </div>
    <details v-if="playerReady">
      <summary class="dn-audio-player__disclaimer-button">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          role="img"
          class="dn-audio-player__disclaimer-icon"
        >
          <title>{{ t('Disclaimer') }}</title>
          <rect
            width="23"
            height="23"
            x=".5"
            y=".5"
            class="dn-audio-player__disclaimer-icon-stroke"
            rx="11.5"
          />
          <path
            class="dn-audio-player__disclaimer-icon-fill"
            fill-rule="evenodd"
            d="M12.003 7.883c.387 0 .701-.31.701-.692a.696.696 0 0 0-.7-.691.696.696 0 0 0-.702.691c0 .382.314.692.701.692M10 9.803h2.353a.5.5 0 0 1 .5.5v5.032h2.104v1H9.75v-1h2.103v-4.532H10z"
            clip-rule="evenodd"
          />
        </svg>
      </summary>
      <p class="dn-audio-player__disclaimer-text">
        {{ t('This AI-generated voice has not affected journalism. Some words may be pronounced imprecisely.') }}
        <dn-link
          v-if="disclaimerLink"
          :href="disclaimerLink"
          :external="true"
          target="_blank"
          class="dn-audio-player__disclaimer-link"
        >
          {{ t('Read more here') }}.
        </dn-link>
      </p>
    </details>
  </div>
</template>

<script setup lang="ts">
import BeyondWordsPlayer from '@beyondwords/player';
import type { PublicationContext } from '~/types';
import { useArticleStore } from '~/store/article';
import { useCommonStore } from '~/store/common';
import { useProfileLoginStore } from '~/store/profileLogin';
const { t } = useI18n();

interface Props {
  articleId: string;
}

const props = defineProps<Props>();
const config = useRuntimeConfig();
const publicConfig = config.public as unknown as PublicationContext;
const playerContainer = ref<HTMLDivElement | null>(null);
const loginTooltipRef = ref<any>(null);
const isLoginTooltipOpen = ref(false);
const loginTooltipKey = ref(0);
const outsideClickHandler = ref<((event: PointerEvent) => void) | null>(null);

const articleStore = useArticleStore();
const commonStore = useCommonStore();
const profileLoginStore = useProfileLoginStore();
const { projectId, disclaimerLink = '' } = publicConfig.beyondWords || {};
const replaceUrl = useUrlReplace().replaceUrl;
const route = useRoute();
const mobileApps = useMobileAppsDetector();
const { genericEvent } = useTrackingEvent();
const cachedContentId = ref<{ articleId: string; value: string | null } | null>(null);

const getContentId = async () => {
  if (cachedContentId.value?.articleId === props.articleId) {
    return cachedContentId.value.value;
  }

  const audioApi = publicConfig.beyondWords?.api;
  if (!audioApi) {
    return null;
  }
  const endpoint = replaceUrl(audioApi, { id: props.articleId }, false);
  const response = await $fetch<{ id?: string }>(endpoint);
  const value = response?.id || null;
  cachedContentId.value = {
    articleId: props.articleId,
    value
  };

  return value;
};
const playerInstance = ref<any>(null);
const playerReady = ref(false);
const playerEventHandles = ref<Array<{ event: string; handle: string }>>([]);
const stopThemeObserver = ref<(() => void) | null>(null);
const MAX_PLAYLIST_ITEMS = publicConfig.beyondWords?.maxPlaylistItems ?? 5;
const isSubscriber = computed(() => {
  return profileLoginStore.loginStatus.isSubscriber || profileLoginStore.loginStatus.isTotalSubscriber;
});
const canListenToAudio = computed(() => {
  return profileLoginStore.loginStatus.isLoggedIn && isSubscriber.value;
});

const audioPlaylistSource = 'audio_playlist_article';

const appendQueryParams = (url: string, params: Record<string, string>) => {
  const isAbsolute = /^https?:\/\//i.test(url);
  const parsed = new URL(url, 'https://local.invalid');

  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      parsed.searchParams.set(key, value);
    }
  });

  if (isAbsolute) {
    return parsed.toString();
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
};

const subscriptionHref = computed(() => {
  const value = (config.public as Record<string, unknown>).subscriptionLink;
  const baseLink = typeof value === 'string' && value.length > 0 ? value : '/subscription/';
  return appendQueryParams(baseLink, {
    source: audioPlaylistSource,
    target: route.fullPath || '/'
  });
});
const loginHref = computed(() => {
  const target = route.fullPath || '/';
  const isDnMobileApp = import.meta.client && publicConfig.name?.toLowerCase() === 'dn' && (commonStore.isAppView || mobileApps.isAndroid() || mobileApps.isIos());

  if (isDnMobileApp) {
    return '/auth/user/login';
  }

  return appendQueryParams('/login', { source: audioPlaylistSource, target });
});

const loginTooltipText = computed(() => {
  if (profileLoginStore.loginStatus.isLoggedIn && !isSubscriber.value) {
    return t('You need an active subscription to listen to this article.');
  }

  return t('You must be logged in with an active subscription to listen to this article.');
});

const loginTooltipCtaText = computed(() => {
  if (profileLoginStore.loginStatus.isLoggedIn && !isSubscriber.value) {
    return t('Subscribe');
  }

  return t('Login');
});

const loginTooltipCtaHref = computed(() => {
  if (profileLoginStore.loginStatus.isLoggedIn && !isSubscriber.value) {
    return subscriptionHref.value;
  }

  return loginHref.value;
});

const loginTooltipCtaTrackEvent = computed(() => ({
  eventName: 'Audio',
  component: 'audio-player',
  elementName: 'dn-audio-login-tooltip-cta',
  elementId: profileLoginStore.loginStatus.isLoggedIn && !isSubscriber.value ? 'audio-subscribe' : 'audio-login',
  linkUrl: loginTooltipCtaHref.value,
  source: audioPlaylistSource,
  target: route.fullPath || '/'
}));

const getPlaylistItems = async () => {
  const streamId = publicConfig.beyondWords?.streamId || publicConfig.kilkaya?.options?.streamId;
  const kilkayaApi = publicConfig.kilkaya?.api;

  if (!streamId || !kilkayaApi) {
    return [];
  }

  try {
    const result = await $fetch<{ items?: Array<{ id?: string }> }>(replaceUrl(kilkayaApi, { streamId }, false), { query: { size: MAX_PLAYLIST_ITEMS } });

    const streamIds = (result?.items || []).map(item => item?.id).filter((id): id is string => !!id);

    // Always include current article first, deduplicate, and limit
    const candidateIds = [...new Set([props.articleId, ...streamIds])].slice(0, MAX_PLAYLIST_ITEMS);

    return candidateIds.map(sourceId => ({ sourceId }));
  } catch (e) {
    return [];
  }
};

const shouldShowAudioPlayer = computed(() => {
  return articleStore.currentArticle?.metadata?.audioFile;
});

const getTooltipPopup = () => {
  const root = loginTooltipRef.value?.$el as HTMLElement | undefined;
  return root?.querySelector('.tooltip') as HTMLElement | null;
};

const unbindOutsideClickHandler = () => {
  if (typeof document === 'undefined') {
    return;
  }
  if (!outsideClickHandler.value) {
    return;
  }
  document.removeEventListener('pointerdown', outsideClickHandler.value, true);
  outsideClickHandler.value = null;
};

const bindOutsideClickHandler = () => {
  if (typeof document === 'undefined') {
    return;
  }
  if (outsideClickHandler.value) {
    return;
  }
  outsideClickHandler.value = (event: PointerEvent) => {
    const tooltipPopup = getTooltipPopup();
    const target = event.target as Node | null;
    if (target instanceof Element && target.closest('.dn-audio-player__login-tooltip-close')) {
      hideLoginTooltip();
      return;
    }
    if (tooltipPopup && target && tooltipPopup.contains(target)) {
      return;
    }
    hideLoginTooltip();
  };
  document.addEventListener('pointerdown', outsideClickHandler.value, true);
};

const showLoginTooltip = async () => {
  isLoginTooltipOpen.value = false;
  unbindOutsideClickHandler();
  await nextTick();
  loginTooltipKey.value += 1;
  isLoginTooltipOpen.value = true;
  await nextTick();
  bindOutsideClickHandler();
  loginTooltipRef.value?.showTooltip?.();
};

const hideLoginTooltip = () => {
  unbindOutsideClickHandler();
  loginTooltipRef.value?.hideTooltip?.();
  isLoginTooltipOpen.value = false;
};

const updatePlayerForScreenSize = () => {
  if (!playerInstance.value) {
    return;
  }

  if (window.innerWidth < 768) {
    playerInstance.value.widgetPosition = 'center';
    playerInstance.value.widgetStyle = 'standard';
  } else {
    playerInstance.value.widgetPosition = 'left';
    playerInstance.value.widgetStyle = 'standard';
  }
};

const cleanupPlayerEventListeners = () => {
  if (playerInstance.value) {
    for (const { event, handle } of playerEventHandles.value) {
      playerInstance.value.removeEventListener(event, handle);
    }
  }
  playerEventHandles.value = [];
};

const initializePlayer = async () => {
  if (!projectId || !props.articleId) {
    return;
  }

  // Container can be unavailable before mount; defer initialization silently.
  if (!playerContainer.value) {
    return;
  }

  try {
    // Clear any previous player
    cleanupPlayerEventListeners();
    playerInstance.value?.destroy();
    playerInstance.value = null;
    playerReady.value = false;
    playerContainer.value.innerHTML = '';

    const playlist = await getPlaylistItems();
    const contentIdValue = playlist.length > 0 ? null : await getContentId();

    if (!playlist.length && !contentIdValue) {
      playerReady.value = false;
      return;
    }

    const getPlayerColors = () => {
      const styles = getComputedStyle(document.documentElement);
      return {
        backgroundColor: styles.getPropertyValue('--color-ui-generic-bg').trim(),
        textColor: styles.getPropertyValue('--color-ui-generic-fg').trim(),
        iconColor: styles.getPropertyValue('--color-ui-generic-fg').trim()
      };
    };

    const colors = getPlayerColors();
    const isMobile = window.innerWidth < 768;
    const initialWidgetPosition = isMobile ? 'center' : 'left';

    await nextTick();

    const player = new BeyondWordsPlayer.Player({
      target: playerContainer.value,
      projectId: Number(projectId),
      ...(playlist.length > 0 ? { playlist } : { contentId: `${contentIdValue}` }),
      callToAction: t('Listen to the article'),
      playlistStyle: 'hide',
      skipButtonStyle: 'seconds-30',
      widgetPosition: initialWidgetPosition,
      logoIconEnabled: false,
      widgetStyle: 'standard',
      ...(publicConfig.beyondWords?.playbackRate != null ? { playbackRate: publicConfig.beyondWords.playbackRate } : {}),
      ...(publicConfig.beyondWords?.playbackRates != null ? { playbackRates: publicConfig.beyondWords.playbackRates } : {}),
      ...(colors.backgroundColor &&
        colors.textColor && {
        backgroundColor: colors.backgroundColor,
        textColor: colors.textColor,
        iconColor: colors.iconColor
      })
    });
    playerInstance.value = player;

    let playTracked = false;
    playerEventHandles.value = [
      {
        event: 'ContentAvailable',
        handle: player.addEventListener('ContentAvailable', () => {
          playerReady.value = true;
        })
      },
      {
        event: 'NoContentAvailable',
        handle: player.addEventListener('NoContentAvailable', () => {
          playerReady.value = false;
        })
      },
      {
        event: 'PlaybackPlaying',
        handle: player.addEventListener('PlaybackPlaying', () => {
          if (!playTracked) {
            playTracked = true;
            genericEvent({
              eventName: 'Audio',
              component: 'audio-player',
              elementName: 'dn-audio-play-button',
              elementId: 'audio-playlist-play',
              source: audioPlaylistSource,
              target: route.fullPath || '/'
            });
          }
        })
      },
      {
        event: '<any>',
        handle: player.addEventListener('<any>', (event: any) => {
          const change = event.changedProps && event.changedProps.contentIndex;
          if (!change) { return; }
          const idx = change.currentValue;
          const item = player.content && player.content[idx];
          genericEvent({
            eventName: 'Audio',
            component: 'audio-player',
            elementName: 'dn-audio-track-change',
            elementId: 'audio-playlist-track-change',
            action: event.type === 'PlaybackEnded' ? 'auto_advance' : 'skip',
            source: audioPlaylistSource,
            target: (item && item.sourceId) || route.fullPath || '/'
          });
        })
      }
    ];

    if (stopThemeObserver.value) {
      stopThemeObserver.value();
      stopThemeObserver.value = null;
    }

    const observerControls = useMutationObserver(
      document.documentElement,
      () => {
        const newColors = getPlayerColors();
        if (newColors.backgroundColor && newColors.textColor) {
          player.backgroundColor = newColors.backgroundColor;
          player.textColor = newColors.textColor;
          player.iconColor = newColors.iconColor;
        }
      },
      {
        attributes: true,
        attributeFilter: ['class']
      }
    );
    stopThemeObserver.value = observerControls.stop;

    updatePlayerForScreenSize();
  } catch (err: any) {
    playerReady.value = false;
  }
};

/*
 * Initialize only when article, body gating, and target container are all
 * ready.
 */
watch(
  [() => props.articleId, () => canListenToAudio.value, () => playerContainer.value],
  ([articleId, canListen, container]) => {
    if (!articleId || !canListen || !container) {
      if (stopThemeObserver.value) {
        stopThemeObserver.value();
        stopThemeObserver.value = null;
      }
      cleanupPlayerEventListeners();
      playerInstance.value?.destroy();
      playerInstance.value = null;
      playerReady.value = false;
      return;
    }

    initializePlayer();
  },
  { immediate: true, flush: 'post' }
);

watch(
  () => canListenToAudio.value,
  (canListen) => {
    if (canListen) {
      hideLoginTooltip();
    }
  }
);

// Initialize on mount
onMounted(() => {
  window.addEventListener('resize', updatePlayerForScreenSize);
});

onUnmounted(() => {
  unbindOutsideClickHandler();

  if (stopThemeObserver.value) {
    stopThemeObserver.value();
    stopThemeObserver.value = null;
  }

  window.removeEventListener('resize', updatePlayerForScreenSize);
  cleanupPlayerEventListeners();
  playerInstance.value?.destroy();
  playerInstance.value = null;
  playerReady.value = false;
});
</script>

<style scoped lang="scss">
.dn-audio-player {
  inline-size: min(var(--content-width), 100vw - calc(var(--site-padding) * 2));
  margin-left: auto;
  margin-right: auto;
  margin-bottom: var(--row-gap-article);
  display: grid;
  grid-template-columns: 1fr 3rem;
  grid-template-rows: 1fr min-content;
  column-gap: 0.5rem;
  padding: 0px;

  details {
    display: contents;
  }
}

.dn-audio-player__disclaimer-button {
  aspect-ratio: 1/1;
  background-color: var(--color-ui-generic-bg);
  block-size: 3rem;
  border-radius: 50%;
  box-sizing: border-box;
  cursor: pointer;
  list-style: none;
  padding: 0.75rem;

  &:hover {
    opacity: 0.8;
  }
}

.dn-audio-player__disclaimer-icon {
  block-size: 1.5rem;
  inline-size: 1.5rem;
}

.dn-audio-player__disclaimer-icon-stroke {
  stroke: var(--color-ui-generic-fg);
}

.dn-audio-player__disclaimer-icon-fill {
  fill: var(--color-ui-generic-fg);
}

.dn-audio-player__disclaimer-text {
  color: var(--color-typography-default);
  font-family: Inter, sans-serif;
  font-size: 0.75rem;
  line-height: 1.33;
  grid-column: 1 / -1;
  margin: 0.25rem 0.125rem 0 0;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.dn-audio-player__disclaimer-link {
  text-decoration: underline;
}

.dn-audio-player__player-wrap {
  position: relative;
  min-width: 0;
}

.dn-audio-player__login-tooltip {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 50;
  pointer-events: none;
}

:deep(.dn-audio-player__login-tooltip .label-container) {
  width: min(320px, calc(100vw - 24px));
  height: 3rem;
  pointer-events: none !important;
}

:deep(.dn-audio-player__login-tooltip .tooltip) {
  background: var(--color-ui-generic-bg, #e5f1ed);
  border-radius: 14px;
  max-width: min(320px, calc(100vw - 24px));
  width: min(320px, calc(100vw - 24px));
  padding: 0;
  box-sizing: border-box;
  pointer-events: auto;
}

:deep(.dn-audio-player__login-tooltip .arrow::before) {
  background: var(--color-ui-generic-bg, #e5f1ed);
}

:deep(.dn-audio-player__login-tooltip .tooltip > span) {
  display: block;
  color: inherit;
  font-size: inherit;
}

:deep(.dn-audio-player__login-tooltip .tooltip[data-popper-placement^='top']) {
  translate: 0 -8px;
}

:deep(.dn-audio-player__login-tooltip .tooltip[data-popper-placement^='bottom']) {
  translate: 0 8px;
}

:deep(.dn-audio-player__login-tooltip [data-popper-placement^='top'] .arrow) {
  left: -135px !important;
}

:deep(.dn-audio-player__login-tooltip [data-popper-placement^='bottom'] .arrow) {
  left: -135px !important;
}

:deep(.dn-audio-player__login-tooltip-card) {
  position: relative;
  padding: 16px;
  color: var(--color-typography-default);
  font-family: var(--typography-editorial-subhead-min-font-family);
}

:deep(.dn-audio-player__login-tooltip-close) {
  position: absolute;
  top: 8px;
  right: 8px;
  border: 0;
  background: transparent;
  color: var(--color-ui-generic-fg);
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  padding: 2px;
}

:deep(.dn-audio-player__login-tooltip-title) {
  margin: 0 28px 8px 0;
  font-size: var(--typography-editorial-subhead-min-font-size);
  line-height: var(--typography-editorial-subhead-min-line-height);
  letter-spacing: var(--typography-editorial-subhead-min-letter-spacing);
  font-weight: var(--typography-editorial-subhead-min-font-weight, 700);
  font-family: var(--typography-editorial-subhead-min-font-family);
}

:deep(.dn-audio-player__login-tooltip-text) {
  margin: 0;
  font-size: 14px;
  line-height: 1.35;
  font-family: inherit;
}

:deep(.dn-audio-player__login-tooltip-cta) {
  display: block !important;
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 6px;
  text-align: center;
  text-decoration: none;
  background: var(--color-button-primary-default-bg, #142b57);
  color: var(--color-button-primary-default-fg, #fff);
  border: 1px solid var(--color-button-primary-default-border, transparent);
  font-size: var(--typography-utility-button-font-size, 16px);
  line-height: var(--typography-utility-button-line-height, 1.2);
  font-family: inherit;
  font-weight: var(--typography-utility-button-font-weight, 600);
}

:deep(.dn-tooltip a.dn-audio-player__login-tooltip-cta) {
  color: var(--color-button-primary-default-fg, #fff);
}

:deep(.dn-audio-player__login-tooltip-cta:hover) {
  background: var(--color-button-primary-hover-bg, var(--color-button-primary-default-bg, #142b57));
  color: var(--color-button-primary-hover-fg, var(--color-button-primary-default-fg, #fff));
  border-color: var(--color-button-primary-hover-border, var(--color-button-primary-default-border, transparent));
}

:deep(.dn-tooltip a.dn-audio-player__login-tooltip-cta:hover) {
  color: var(--color-button-primary-hover-fg, var(--color-button-primary-default-fg, #fff));
}

:deep(.dn-audio-player__login-tooltip-cta:active) {
  background: var(--color-button-primary-active-bg, var(--color-button-primary-hover-bg, var(--color-button-primary-default-bg, #142b57)));
  color: var(--color-button-primary-active-fg, var(--color-button-primary-hover-fg, var(--color-button-primary-default-fg, #fff)));
  border-color: var(--color-button-primary-active-border, var(--color-button-primary-hover-border, var(--color-button-primary-default-border, transparent)));
}

:deep(.dn-tooltip a.dn-audio-player__login-tooltip-cta:active) {
  color: var(--color-button-primary-active-fg, var(--color-button-primary-hover-fg, var(--color-button-primary-default-fg, #fff)));
}

@media only screen and (width <= 430px) {
  :deep(.dn-audio-player__login-tooltip .label-container) {
    width: 100%;
  }

  :deep(.dn-audio-player__login-tooltip .tooltip) {
    width: 100% !important;
    max-width: 100% !important;
  }

  :deep(.dn-audio-player__login-tooltip [data-popper-placement^='top'] .arrow) {
    left: 20px !important;
    transform: none !important;
  }

  :deep(.dn-audio-player__login-tooltip .tooltip[data-popper-placement^='top']) {
    translate: 0 -8px;
  }

  :deep(.dn-audio-player__login-tooltip .tooltip[data-popper-placement^='bottom']) {
    translate: 0 8px;
  }

  :deep(.dn-audio-player__login-tooltip [data-popper-placement^='bottom'] .arrow) {
    left: 20px !important;
    transform: none !important;
  }
}

@media only screen and (width <= 768px) {
  :deep(.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp.bwp .secondary-buttons) {
    display: flex !important;
  }
}

.dn-audio-player__placeholder {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dn-audio-player__placeholder-container {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px;
  background: var(--color-ui-generic-bg);
  color: var(--color-ui-generic-fg);
  border-radius: 25px;
  font-size: 12px;
  font-weight: 500;
  font-family: InterVariable, sans-serif;

  @media only screen and (width >= 769px) {
    width: 100%;
  }
}

.dn-audio-player__placeholder-button {
  flex: 0 0 auto;
  inline-size: 40px;
  block-size: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  appearance: none;
  overflow: hidden;
  cursor: pointer;
}

.dn-audio-player__placeholder-icon {
  inline-size: 40px;
  block-size: 40px;
  fill: var(--color-ui-generic-fg);
}
</style>
